/**
 * Browser side of the `pit-directory` edge proxy (pit#274, scr-central#431).
 *
 * When VITE_DIRECTORY_READS=on, staff identity fields (name, email, department)
 * come from the canonical directory via the edge function instead of the
 * copied columns on pit.staff. Off (the default) leaves rows untouched, so
 * rollback is a single env change. The edge function enforces its own flag too.
 *
 * One batched call per screen load (chunks of 200), never per row. On any
 * failure the local values are kept, so a directory outage degrades to the
 * current behaviour instead of blanking names.
 */
import { supabase } from '../supabase/config';

export const MAX_IDS_PER_CALL = 200;

export interface DirectoryStaff {
  person_id: string;
  display_name: string | null;
  email: string | null;
  department: string | null;
}

type Invoke = (body: { op: 'staffByIds'; ids: string[] }) => Promise<{
  data: { staff?: DirectoryStaff[] } | null;
  error: unknown;
}>;

export function directoryReadsEnabled(
  env: Record<string, string | undefined> = import.meta.env as Record<string, string | undefined>,
): boolean {
  return (env.VITE_DIRECTORY_READS ?? 'off').toLowerCase() === 'on';
}

const defaultInvoke: Invoke = (body) =>
  supabase.functions.invoke('pit-directory', { body }) as ReturnType<Invoke>;

/** Fetches directory staff for the given person ids. Returns an empty map on failure. */
export async function fetchStaffByIds(
  ids: string[],
  invoke: Invoke = defaultInvoke,
): Promise<Map<string, DirectoryStaff>> {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  const out = new Map<string, DirectoryStaff>();
  for (let i = 0; i < unique.length; i += MAX_IDS_PER_CALL) {
    const chunk = unique.slice(i, i + MAX_IDS_PER_CALL);
    const { data, error } = await invoke({ op: 'staffByIds', ids: chunk });
    if (error || !data?.staff) {
      // No PII in the log: just the fact and the batch size.
      console.warn('directoryProxy: staffByIds unavailable; using local values', { batch: chunk.length });
      return new Map();
    }
    for (const s of data.staff) out.set(s.person_id, s);
  }
  return out;
}

type StaffLike = {
  person_id?: string | null;
  name?: string | null;
  email?: string | null;
  department?: string | null;
};

/**
 * Overlays directory name/email/department onto staff rows that have a
 * person_id. Rows without a link, or missing from the directory, keep their
 * local values. Local PK and FKs are untouched.
 */
export async function withDirectoryFields<T extends StaffLike>(
  rows: T[],
  opts: { enabled?: boolean; invoke?: Invoke } = {},
): Promise<T[]> {
  const enabled = opts.enabled ?? directoryReadsEnabled();
  if (!enabled || rows.length === 0) return rows;
  const ids = rows.map((r) => r.person_id).filter((x): x is string => !!x);
  if (ids.length === 0) return rows;
  const dir = await fetchStaffByIds(ids, opts.invoke);
  if (dir.size === 0) return rows;
  return rows.map((r) => {
    const d = r.person_id ? dir.get(r.person_id) : undefined;
    if (!d) return r;
    return {
      ...r,
      name: d.display_name ?? r.name,
      email: d.email ?? r.email,
      department: d.department ?? r.department,
    };
  });
}
